export interface ColorTheme {
  id: string;
  nameAr: string;
  nameEn: string;
  hue: number;
  saturation: number;
  /** If true, use dual-tone: navy primary + orange accent */
  dualTone?: boolean;
  accentHue?: number;
  accentSaturation?: number;
}

export const COLOR_THEMES: ColorTheme[] = [
  { id: 'default', nameAr: 'كحلي-برتقالي', nameEn: 'Default',  hue: 220, saturation: 55, dualTone: true, accentHue: 25, accentSaturation: 85 },
  { id: 'orange',  nameAr: 'برتقالي',       nameEn: 'Orange',   hue: 25,  saturation: 80 },
  { id: 'navy',    nameAr: 'كحلي',          nameEn: 'Navy',     hue: 220, saturation: 50 },
  { id: 'slate',   nameAr: 'رمادي',         nameEn: 'Slate',    hue: 215, saturation: 25 },
  { id: 'blue',    nameAr: 'أزرق',          nameEn: 'Blue',     hue: 220, saturation: 65 },
  { id: 'indigo',  nameAr: 'نيلي',          nameEn: 'Indigo',   hue: 243, saturation: 65 },
  { id: 'teal',    nameAr: 'فيروزي',        nameEn: 'Teal',     hue: 174, saturation: 55 },
  { id: 'emerald', nameAr: 'زمردي',         nameEn: 'Emerald',  hue: 152, saturation: 57 },
  { id: 'rose',    nameAr: 'وردي',          nameEn: 'Rose',     hue: 347, saturation: 65 },
  { id: 'amber',   nameAr: 'عنبري',         nameEn: 'Amber',    hue: 43,  saturation: 90 },
  { id: 'purple',  nameAr: 'بنفسجي',       nameEn: 'Purple',   hue: 268, saturation: 60 },
];

const STORAGE_KEY = 'app-color-theme';
const BRIGHTNESS_KEY = 'app-brightness';

// brightness: 0 = darkest, 100 = lightest, 50 = default
export function applyColorTheme(theme: ColorTheme, brightness?: number) {
  const savedBrightness = brightness ?? getSavedBrightness();
  applyBrightness(savedBrightness, theme);
}

export function applyBrightness(brightness: number, theme?: ColorTheme) {
  const t = theme ?? getSavedTheme();
  const root = document.documentElement;

  if (t.dualTone) {
    applyDualTone(brightness, t);
    return;
  }

  const { hue: h, saturation: s } = t;

  const bgL   = 92 + (brightness / 100) * 14;
  const bgLc  = Math.min(bgL, 99.5);
  const cardL = Math.min(bgLc + 1, 100);
  const borderL = 80 + (brightness / 100) * 18;
  const mutedL  = 88 + (brightness / 100) * 12;

  // Light mode
  root.style.setProperty('--primary', `${h} ${s}% 58%`);
  root.style.setProperty('--primary-foreground', '0 0% 100%');
  root.style.setProperty('--ring', `${h} ${s}% 58%`);
  root.style.setProperty('--background', `${h} 20% ${bgLc.toFixed(1)}%`);
  root.style.setProperty('--foreground', `${h} 50% 10%`);
  root.style.setProperty('--card', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--card-foreground', `${h} 50% 10%`);
  root.style.setProperty('--popover', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--popover-foreground', `${h} 50% 10%`);
  root.style.setProperty('--secondary', `${h} 30% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--secondary-foreground', `${h} 50% 15%`);
  root.style.setProperty('--muted', `${h} 20% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--muted-foreground', `${h} 10% 45%`);
  root.style.setProperty('--accent', `${h} 35% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--accent-foreground', `${h} 50% 15%`);
  root.style.setProperty('--border', `${h} 20% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--input', `${h} 20% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-background', `${h} 25% ${Math.min(bgLc + 1.5, 100).toFixed(1)}%`);
  root.style.setProperty('--sidebar-foreground', `${h} 30% 20%`);
  root.style.setProperty('--sidebar-primary', `${h} ${s}% 58%`);
  root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%');
  root.style.setProperty('--sidebar-accent', `${h} 30% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-accent-foreground', `${h} 40% 15%`);
  root.style.setProperty('--sidebar-border', `${h} 15% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-ring', `${h} ${s}% 58%`);
  root.style.setProperty('--chart-1', `${h} 70% 55%`);
  root.style.setProperty('--chart-2', `${h + 22} 60% 50%`);
  root.style.setProperty('--chart-3', `${h - 28} 55% 55%`);

  // Dark mode
  const darkBgL   = 3 + (brightness / 100) * 10;
  const darkCardL = darkBgL + 2;
  const darkBorderL = darkBgL + 10;
  const darkMutedL  = darkBgL + 9;

  const styleId = 'dynamic-dark-theme';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .dark {
      --primary: ${h} ${s}% 62% !important;
      --primary-foreground: 0 0% 100% !important;
      --ring: ${h} ${s}% 62% !important;
      --background: ${h} 30% ${darkBgL.toFixed(1)}% !important;
      --foreground: ${h} 10% 95% !important;
      --card: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --card-foreground: ${h} 10% 95% !important;
      --popover: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --popover-foreground: ${h} 10% 95% !important;
      --secondary: ${h} 25% ${darkMutedL.toFixed(1)}% !important;
      --secondary-foreground: ${h} 10% 95% !important;
      --muted: ${h} 25% ${darkMutedL.toFixed(1)}% !important;
      --muted-foreground: ${h} 10% 60% !important;
      --accent: ${h} 25% ${darkMutedL.toFixed(1)}% !important;
      --accent-foreground: ${h} 10% 95% !important;
      --border: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --input: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --sidebar-background: ${h} 30% ${darkCardL.toFixed(1)}% !important;
      --sidebar-foreground: ${h} 10% 90% !important;
      --sidebar-primary: ${h} ${s}% 62% !important;
      --sidebar-primary-foreground: 0 0% 100% !important;
      --sidebar-accent: ${h} 20% ${(darkBgL + 6).toFixed(1)}% !important;
      --sidebar-accent-foreground: ${h} 10% 90% !important;
      --sidebar-border: ${h} 20% ${(darkBgL + 6).toFixed(1)}% !important;
      --sidebar-ring: ${h} ${s}% 62% !important;
    }
  `;

  localStorage.setItem(STORAGE_KEY, t.id);
  localStorage.setItem(BRIGHTNESS_KEY, String(brightness));
  window.dispatchEvent(new CustomEvent('color-theme-changed', { detail: t.id }));
}

/** Dual-tone theme: navy primary + orange accent */
function applyDualTone(brightness: number, t: ColorTheme) {
  const h = t.hue;           // navy 220
  const s = t.saturation;    // 55
  const ah = t.accentHue!;   // orange 25
  const as = t.accentSaturation!; // 85
  const root = document.documentElement;

  const bgL   = 92 + (brightness / 100) * 14;
  const bgLc  = Math.min(bgL, 99.5);
  const cardL = Math.min(bgLc + 1, 100);
  const borderL = 80 + (brightness / 100) * 18;
  const mutedL  = 88 + (brightness / 100) * 12;

  // Light mode — primary = navy, accent/sidebar-primary = orange
  root.style.setProperty('--primary', `${h} ${s}% 24%`);
  root.style.setProperty('--primary-foreground', '0 0% 100%');
  root.style.setProperty('--ring', `${h} ${s}% 24%`);
  root.style.setProperty('--background', `${h} 15% ${bgLc.toFixed(1)}%`);
  root.style.setProperty('--foreground', `${h} 45% 10%`);
  root.style.setProperty('--card', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--card-foreground', `${h} 45% 10%`);
  root.style.setProperty('--popover', `0 0% ${cardL.toFixed(1)}%`);
  root.style.setProperty('--popover-foreground', `${h} 45% 10%`);
  root.style.setProperty('--secondary', `${h} 18% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--secondary-foreground', `${h} 40% 14%`);
  root.style.setProperty('--muted', `${h} 12% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--muted-foreground', `${h} 10% 42%`);
  root.style.setProperty('--accent', `${ah} ${as}% 52%`);
  root.style.setProperty('--accent-foreground', '0 0% 100%');
  root.style.setProperty('--border', `${h} 18% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--input', `${h} 18% ${borderL.toFixed(1)}%`);

  // Sidebar — light background matching page
  root.style.setProperty('--sidebar-background', `${h} 15% ${Math.min(bgLc + 1.5, 100).toFixed(1)}%`);
  root.style.setProperty('--sidebar-foreground', `${h} 45% 15%`);
  root.style.setProperty('--sidebar-primary', `${h} ${s}% 24%`);
  root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%');
  root.style.setProperty('--sidebar-accent', `${h} 18% ${mutedL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-accent-foreground', `${h} 40% 15%`);
  root.style.setProperty('--sidebar-border', `${h} 18% ${borderL.toFixed(1)}%`);
  root.style.setProperty('--sidebar-ring', `${h} ${s}% 24%`);

  root.style.setProperty('--chart-1', `${h} ${s}% 30%`);
  root.style.setProperty('--chart-2', `${ah} ${as}% 52%`);
  root.style.setProperty('--chart-3', `${h} 20% 55%`);

  // Dark mode
  const darkBgL   = 3 + (brightness / 100) * 10;
  const darkCardL = darkBgL + 2;
  const darkBorderL = darkBgL + 10;
  const darkMutedL  = darkBgL + 9;

  const styleId = 'dynamic-dark-theme';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .dark {
      --primary: ${ah} ${as}% 52% !important;
      --primary-foreground: 0 0% 100% !important;
      --ring: ${ah} ${as}% 52% !important;
      --background: ${h} 30% ${darkBgL.toFixed(1)}% !important;
      --foreground: ${h} 10% 94% !important;
      --card: ${h} 28% ${darkCardL.toFixed(1)}% !important;
      --card-foreground: ${h} 10% 94% !important;
      --popover: ${h} 28% ${darkCardL.toFixed(1)}% !important;
      --popover-foreground: ${h} 10% 94% !important;
      --secondary: ${h} 22% ${darkMutedL.toFixed(1)}% !important;
      --secondary-foreground: ${h} 10% 92% !important;
      --muted: ${h} 22% ${darkMutedL.toFixed(1)}% !important;
      --muted-foreground: ${h} 10% 55% !important;
      --accent: ${h} 25% ${(darkMutedL + 2).toFixed(1)}% !important;
      --accent-foreground: ${h} 10% 92% !important;
      --border: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --input: ${h} 20% ${darkBorderL.toFixed(1)}% !important;
      --sidebar-background: ${h} 30% ${(darkBgL + 1).toFixed(1)}% !important;
      --sidebar-foreground: ${h} 10% 90% !important;
      --sidebar-primary: ${ah} ${as}% 52% !important;
      --sidebar-primary-foreground: 0 0% 100% !important;
      --sidebar-accent: ${h} 25% ${(darkBgL + 6).toFixed(1)}% !important;
      --sidebar-accent-foreground: ${h} 10% 90% !important;
      --sidebar-border: ${h} 22% ${(darkBgL + 5).toFixed(1)}% !important;
      --sidebar-ring: ${ah} ${as}% 52% !important;
    }
  `;

  localStorage.setItem(STORAGE_KEY, t.id);
  localStorage.setItem(BRIGHTNESS_KEY, String(brightness));
  window.dispatchEvent(new CustomEvent('color-theme-changed', { detail: t.id }));
}

export function getSavedThemeId(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  // Migrate old defaults
  if (!saved || saved === 'orange' || saved === 'titan') {
    localStorage.setItem(STORAGE_KEY, 'default');
    return 'default';
  }
  return saved;
}

export function getSavedTheme(): ColorTheme {
  const id = getSavedThemeId();
  return COLOR_THEMES.find(t => t.id === id) || COLOR_THEMES[0];
}

export function getSavedBrightness(): number {
  const val = localStorage.getItem(BRIGHTNESS_KEY);
  return val !== null ? Number(val) : 50;
}

export function getSavedDarkMode(): boolean {
  return localStorage.getItem('app-dark-mode') === 'true';
}

export function saveDarkModeLocal(dark: boolean) {
  localStorage.setItem('app-dark-mode', String(dark));
}

// Apply all theme preferences at once (used on login)
export function applyAllPreferences(themeId: string, brightness: number, darkMode: boolean) {
  const theme = COLOR_THEMES.find(t => t.id === themeId) || COLOR_THEMES[0];
  localStorage.setItem(STORAGE_KEY, theme.id);
  localStorage.setItem(BRIGHTNESS_KEY, String(brightness));
  localStorage.setItem('app-dark-mode', String(darkMode));
  applyBrightness(brightness, theme);
}
